// src/components/layout/Applyuit.js
import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import Navbar from "./Navbar";
import "../../styles/applyform.css";

const Applyuit = () => {
  const formRef = useRef(null);
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ STRICT VALIDATION FUNCTION
  const validateForm = () => {
    const newErrors = {};
    const formData = new FormData(formRef.current);
    
    // Name - letters only
    const name = formData.get('name')?.trim();
    if (!name || name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(name)) {
      newErrors.name = 'Name can only contain letters';
    }

    // Email validation
    const email = formData.get('email')?.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone - exactly 10 digits
    if (phone.length !== 10) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }

    // City - letters only
    const city = formData.get('city')?.trim();
    if (!city || city.length < 2) {
      newErrors.city = 'City must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(city)) {
      newErrors.city = 'City can only contain letters';
    }

    // Course
    const course = formData.get('course');
    if (!course || course === '') {
      newErrors.course = 'Please select a course';
    }

    // Qualification
    const qualification = formData.get('qualification')?.trim();
    if (!qualification) {
      newErrors.qualification = 'Please enter your qualification';
    }

    // Marks
    const marks = formData.get('marks')?.trim();
    if (!marks) {
      newErrors.marks = 'Please enter your marks/CGPA';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.getElementById(firstErrorField === 'name' ? 'fullName' : firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      return;
    }

    setLoading(true);

    const formData = new FormData(formRef.current);
    const templateParams = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      city: formData.get('city'),
      course: formData.get('course'),
      qualification: formData.get('qualification'),
      marks: formData.get('marks'),
      message: formData.get('message') || 'No message',
      chosen_college: 'UIT College',
      college_name: 'UIT College',
      form_source: 'uit_dedicated_page',
      page_url: window.location.href,
      submission_time: new Date().toLocaleString('en-IN', { 
        timeZone: 'Asia/Kolkata',
        dateStyle: 'medium',
        timeStyle: 'short'
      })
    };

    emailjs
      .send('service_u0g0bqj', 'template_naso7ph', templateParams, '0JNndpSn5KZm_5yLJ')
      .then(() => {
        setLoading(false);
        setShowSuccess(true);
        formRef.current.reset();
        setPhone("");
        setErrors({});
        setTimeout(() => setShowSuccess(false), 5000);
      })
      .catch((error) => {
        setLoading(false);
        console.error('EmailJS error', error);
        alert('Failed to submit. Please try again.');
      });
  };

  return (
    <>
      <Navbar />
      <div className="apply-page-wrapper" style={{ backgroundImage: 'url(/images/uitapply.png)' }}>
        <div className="fake-navbar-container">
          <img src="/images/uitapplynav.png" alt="University Navigation" className="fake-navbar-img" />
        </div>

        <div className="apply-container">
          <div className="left-banner">
            <h1 className="banner-title">
              Break the wall between you and a successful career!
            </h1>
            <p className="banner-text">
              At Uttaranchal University, you will be at the center of a dynamic
              learning community amidst scenic surroundings and a peaceful environment.
            </p>
            <div className="banner-footer">
              <span className="admission-label">Admission</span>
              <h2 className="admission-year">Open 2026-27</h2>
              <div className="notification-box">
                <span className="pulse-dot"></span>
                Our Admission Department is Open on Sundays.
              </div>
            </div>
          </div>

          <div className="apply-card">
            <div className="apply-header">
              <span className="apply-subtitle">Build your Career</span>
              <h3 className="apply-title">Register Today to Get Updates</h3>
            </div>

            <div className="apply-body">
              {!showSuccess ? (
                <form ref={formRef} className="register-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div style={{ flex: 1 }}>
                      <input
                        id="fullName"
                        name="name"
                        type="text"
                        placeholder="Enter Full Name (letters only)"
                        className={`form-input ${errors.name ? 'input-error' : ''}`}
                        required
                      />
                      {errors.name && <p className="error-text">{errors.name}</p>}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter Email ID"
                        className={`form-input ${errors.email ? 'input-error' : ''}`}
                        required
                      />
                      {errors.email && <p className="error-text">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="mobile-group" style={{ flex: 1 }}>
                      <span className="country-code">
                        <img src="https://flagcdn.com/w20/in.png" alt="India" width="20" /> +91
                      </span>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="Mobile Number (10 digits)"
                        className={`form-input mobile-input ${errors.phone ? 'input-error' : ''}`}
                        value={phone}
                        maxLength="10"
                        required
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setPhone(digits);
                          setErrors({ ...errors, phone: '' });
                        }}
                      />
                      {errors.phone && <p className="error-text">{errors.phone}</p>}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <input
                        id="city"
                        name="city"
                        type="text"
                        placeholder="City"
                        className={`form-input ${errors.city ? 'input-error' : ''}`}
                        required
                      />
                      {errors.city && <p className="error-text">{errors.city}</p>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div style={{ flex: 1 }}>
                      <select 
                        id="course" 
                        name="course" 
                        className={`form-select ${errors.course ? 'input-error' : ''}`}
                        defaultValue="" 
                        required
                      >
                        <option value="" disabled>Select a course</option>
                        <option value="btech-cse">B.Tech CSE</option>
                        <option value="bba">BBA</option>
                        <option value="bca">BCA</option>
                        <option value="mba">MBA</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.course && <p className="error-text">{errors.course}</p>}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <input
                        id="qualification"
                        name="qualification"
                        type="text"
                        placeholder="Current Qualification"
                        className={`form-input ${errors.qualification ? 'input-error' : ''}`}
                        required
                      />
                      {errors.qualification && <p className="error-text">{errors.qualification}</p>}
                    </div>
                  </div>

                  <div className="form-row">
                    <input
                      id="marks"
                      name="marks"
                      type="text"
                      placeholder="Approx. Percentage / CGPA"
                      className={`form-input ${errors.marks ? 'input-error' : ''}`}
                      required
                    />
                    {errors.marks && <p className="error-text">{errors.marks}</p>}
                  </div>

                  <div className="form-row">
                    <textarea
                      id="message"
                      name="message"
                      rows="3"
                      placeholder="Message / Query (optional)"
                      className="form-input"
                    />
                  </div>

                  <button type="submit" className="submit-btn" disabled={loading}>
                    {loading ? 'SUBMITTING...' : 'SUBMIT'}
                  </button>
                </form>
              ) : (
                <div className="uit-success-message">
                  <div className="uit-success-icon">✓</div>
                  <h3 className="uit-success-title">Application Submitted!</h3>
                  <p className="uit-success-text">
                    Thank you for registering at UIT College. Our team will contact you shortly.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Applyuit;
